import { createGlobalState } from '@vueuse/core';
import { ref, computed } from 'vue';
import { campaignsClient } from '~/services';
import { catchRequestError } from '~/utils/request';
import { useToasted } from '~/composables/useToasted';
import dayjs from '~/utils/dayjs';
import { getClientTz } from '~/utils/date';
import { isCancel } from 'axios';
import type { VxDateTimePickerProps, VxPopoverPlacement } from '@voxie/frontend-components';

export const REASON_MESSAGES: Record<string, string> = {
    reserved_date: 'This date has been reserved by Corporate.',
    too_soon: 'Date unavailable. Corporate requires scheduling further in advance.',
    too_far_out: 'Date unavailable. Corporate only allows scheduling within a limited timeframe.',
    outside_permitted_window: "This date and time falls outside Corporate's approved sending window.",
    frequency_limit_reached: "You've reached your campaign limit for this period.",
};

type RawCalendarDates = Record<
    string,
    {
        allowed: boolean;
        reasons: string[];
    }
>;

type ActiveMonth = {
    month: number;
    year: number;
};

interface SendingCalendarContext {
    groupId?: string;
    orgUnitId?: number;
}

type DateAttributes = {
    dates: Date[];
    popover:
        | {
              placement: VxPopoverPlacement;
              hover: boolean;
              arrow: boolean;
          }
        | true;
};

interface ParsedCalendarData {
    disabledDates: Date[];
    dateAttributes: DateAttributes[];
    allowedHoursByDate: Map<string, number[]>;
    blockedHoursByDate: Map<string, Set<number>>;
    reasonsByDate: Map<string, Set<string>>;
}

function parseCalendarResponse(calendar: RawCalendarDates, startDate: string, endDate: string): ParsedCalendarData {
    const clientTimezone = getClientTz();
    const start = dayjs.tz(startDate, clientTimezone);
    const end = dayjs.tz(endDate, clientTimezone);

    const allDates = new Set<string>();
    const dateMap = new Map<string, Set<number>>();
    const blockedHoursByDate = new Map<string, Set<number>>();
    const reasonsByDate = new Map<string, Set<string>>();

    const timestamps = Object.keys(calendar);

    for (const timestamp of timestamps) {
        const slot = calendar[timestamp];
        if (!slot) {
            continue;
        }

        const dateTimeInGroupTz = dayjs.utc(timestamp).tz(clientTimezone);
        const dateKey = dateTimeInGroupTz.format('YYYY-MM-DD');

        if (dateTimeInGroupTz.isBefore(start, 'date') || dateTimeInGroupTz.isAfter(end, 'date')) {
            continue;
        }

        const hour = dateTimeInGroupTz.hour();

        allDates.add(dateKey);

        if (slot.allowed) {
            if (!dateMap.has(dateKey)) {
                dateMap.set(dateKey, new Set());
            }

            dateMap.get(dateKey)!.add(hour);
        } else {
            if (!blockedHoursByDate.has(dateKey)) {
                blockedHoursByDate.set(dateKey, new Set());
            }

            blockedHoursByDate.get(dateKey)!.add(hour);

            if (!reasonsByDate.has(dateKey)) {
                reasonsByDate.set(dateKey, new Set());
            }
            for (const reason of slot.reasons) {
                reasonsByDate.get(dateKey)!.add(reason);
            }
        }
    }

    for (const [dateKey, blockedHours] of blockedHoursByDate.entries()) {
        const allowedHours = dateMap.get(dateKey);
        if (allowedHours) {
            for (const hour of allowedHours) {
                blockedHours.delete(hour);
            }
            if (blockedHours.size === 0) {
                blockedHoursByDate.delete(dateKey);
            }
        }
    }

    const disabledDates: Date[] = [];
    for (const dateKey of allDates) {
        if (!dateMap.has(dateKey)) {
            disabledDates.push(dayjs.tz(dateKey, clientTimezone).toDate());
        }
    }

    const dateAttributes: DateAttributes[] =
        disabledDates.length > 0
            ? [
                  {
                      dates: disabledDates,
                      popover: {
                          placement: 'top',
                          hover: false,
                          arrow: true,
                      },
                  },
              ]
            : [];

    const allowedHoursByDate = new Map<string, number[]>();
    for (const [dateKey, hours] of dateMap.entries()) {
        allowedHoursByDate.set(dateKey, Array.from(hours));
    }

    return {
        disabledDates,
        dateAttributes,
        allowedHoursByDate,
        blockedHoursByDate,
        reasonsByDate,
    };
}

export const useSendingCalendar = createGlobalState(() => {
    const teamId = Spark.state.currentTeam.id;
    const toasted = useToasted();

    const rawCalendarDates = ref<RawCalendarDates | null>(null);
    const loadingCalendar = ref<boolean>(false);
    const loadedMonths = ref<Set<string>>(new Set());
    const parsedCalendarByMonth = ref<Map<string, ParsedCalendarData>>(new Map());
    const cancelKey = Symbol('sending-calendar');

    const isMonthLoaded = ({ year, month }: ActiveMonth): boolean => {
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        return loadedMonths.value.has(monthKey);
    };

    const isMonthParsed = ({ year, month }: ActiveMonth): boolean => {
        const monthKey = `${year}-${String(month).padStart(2, '0')}`;
        return parsedCalendarByMonth.value.has(monthKey);
    };

    const getSendingCalendar = async (
        context: SendingCalendarContext,
        audienceType: string,
        activeMonth: ActiveMonth
    ): Promise<void> => {
        const { groupId, orgUnitId } = context;

        if (!groupId && !orgUnitId) {
            return;
        }

        if (isMonthLoaded(activeMonth)) {
            return;
        }

        const now = dayjs().tz(getClientTz());
        const currentMonth = now.get('month');
        const currentYear = now.get('year');
        const currentPageDate = dayjs()
            .tz(getClientTz())
            .year(activeMonth.year)
            .month(activeMonth.month - 1);

        const isCurrentMonth = activeMonth.year === currentYear && activeMonth.month - 1 === currentMonth;
        const startDate = isCurrentMonth
            ? now.format('YYYY-MM-DD')
            : currentPageDate.startOf('month').format('YYYY-MM-DD');
        const endDate = currentPageDate.endOf('month').format('YYYY-MM-DD');

        loadingCalendar.value = true;

        try {
            const params = {
                start_date: startDate,
                end_date: endDate,
            };

            const response = groupId
                ? await campaignsClient.groupCampaignsSendingCalendar(teamId, groupId, audienceType, params, {
                      cancelKey,
                  })
                : await campaignsClient.orgUnitCampaignsSendingCalendar(teamId, orgUnitId, audienceType, params, {
                      cancelKey,
                  });

            rawCalendarDates.value = response.data.calendar;

            const monthKey = currentPageDate.format('YYYY-MM');

            loadedMonths.value.add(monthKey);

            if (rawCalendarDates.value) {
                const parsedData = parseCalendarResponse(rawCalendarDates.value, startDate, endDate);
                parsedCalendarByMonth.value.set(monthKey, parsedData);
            }
        } catch (e) {
            if (!isCancel(e)) {
                toasted.error(catchRequestError(e));
            }
        } finally {
            loadingCalendar.value = false;
        }
    };

    const disabledDates = computed(() => {
        const allDisabledDates: Date[] = [];
        for (const parsedData of parsedCalendarByMonth.value.values()) {
            allDisabledDates.push(...parsedData.disabledDates);
        }
        return allDisabledDates;
    });

    const dateAttributes = computed((): VxDateTimePickerProps['attributes'] => {
        const allDates: Date[] = [];
        for (const parsedData of parsedCalendarByMonth.value.values()) {
            for (const attr of parsedData.dateAttributes) {
                allDates.push(...attr.dates);
            }
        }

        return allDates.length > 0
            ? [
                  {
                      dates: allDates,
                      popover: {
                          placement: 'top',
                      },
                  },
              ]
            : [];
    });

    const allowedHoursByDate = computed(() => {
        const merged = new Map<string, number[]>();
        for (const parsedData of parsedCalendarByMonth.value.values()) {
            for (const [dateKey, hours] of parsedData.allowedHoursByDate.entries()) {
                merged.set(dateKey, hours);
            }
        }
        return merged;
    });

    const blockedHoursByDate = computed(() => {
        const merged = new Map<string, Set<number>>();
        for (const parsedData of parsedCalendarByMonth.value.values()) {
            for (const [dateKey, hours] of parsedData.blockedHoursByDate.entries()) {
                merged.set(dateKey, hours);
            }
        }
        return merged;
    });

    const reasonsByDate = computed(() => {
        const merged = new Map<string, Set<string>>();
        for (const parsedData of parsedCalendarByMonth.value.values()) {
            for (const [dateKey, reasons] of parsedData.reasonsByDate.entries()) {
                merged.set(dateKey, reasons);
            }
        }
        return merged;
    });

    const getBoundaryHours = (allowedHours: number[]): Set<number> => {
        if (!allowedHours || allowedHours.length === 0) {
            return new Set();
        }

        const sorted = [...allowedHours].sort((a, b) => a - b);
        const boundaries = new Set<number>();

        for (let i = 0; i < sorted.length; i++) {
            const currentHour = sorted[i];
            const nextHour = sorted[i + 1];

            if (currentHour !== undefined && (nextHour === undefined || nextHour !== currentHour + 1)) {
                boundaries.add(currentHour);
            }
        }

        return boundaries;
    };

    const findClosestAvailableTime = (): Date | undefined => {
        if (!allowedHoursByDate.value || allowedHoursByDate.value.size === 0) {
            return undefined;
        }

        const tz = getClientTz();
        const now = dayjs().tz(tz);
        const currentMinutes = now.minute();
        const roundedMinutes = Math.ceil(currentMinutes / 5) * 5;
        const availableTimes: dayjs.Dayjs[] = [];

        for (const [dateKey, hours] of allowedHoursByDate.value.entries()) {
            const date = dayjs.tz(dateKey, tz);

            for (const hour of hours) {
                const isSameHourAsNow = date.isSame(now, 'day') && hour === now.hour();
                const minutes = isSameHourAsNow ? roundedMinutes : 0;
                const dateTime = date.hour(hour).minute(minutes).second(0);

                if (dateTime.isAfter(now)) {
                    availableTimes.push(dateTime);
                }
            }
        }

        if (availableTimes.length === 0) {
            return undefined;
        }

        availableTimes.sort((a, b) => a.valueOf() - b.valueOf());

        return availableTimes[0]?.toDate();
    };

    const isDateTimeAllowed = (dateTime: Date | string): boolean => {
        if (!allowedHoursByDate.value || allowedHoursByDate.value.size === 0) {
            return true;
        }

        const tz = getClientTz();
        const dt = typeof dateTime === 'string' ? dayjs(dateTime).tz(tz) : dayjs(dateTime).tz(tz);
        const dateKey = dt.format('YYYY-MM-DD');
        const hour = dt.hour();
        const minute = dt.minute();

        const allowedHours = allowedHoursByDate.value.get(dateKey);

        if (!allowedHours || !allowedHours.includes(hour)) {
            return false;
        }

        const boundaryHours = getBoundaryHours(allowedHours);

        if (boundaryHours.has(hour)) {
            return minute === 0;
        }

        return true;
    };

    const clearData = () => {
        rawCalendarDates.value = null;
        parsedCalendarByMonth.value = new Map();
        loadedMonths.value = new Set();
    };

    return {
        rawCalendarDates,
        loadingCalendar,
        getSendingCalendar,
        isMonthLoaded,
        isMonthParsed,
        loadedMonths,
        parsedCalendarByMonth,

        disabledDates,
        dateAttributes,
        allowedHoursByDate,
        blockedHoursByDate,
        reasonsByDate,
        findClosestAvailableTime,
        isDateTimeAllowed,

        clearData,
    };
});
